/**
 * Context for managing and sharing the active section state of the sidebar in the application.
 * This context helps to determine which section of the sidebar is currently active or selected.
 */

import { createContext, useState } from 'react';

/**
 * @typedef {Object} SidebarContextType
 * @property {String|null} activeSection - The currently active section in the sidebar. Can be null if no section is active.
 * @property {Function} setActiveSection - Function to update the active section in the sidebar.
 */

/** @type {React.Context<SidebarContextType>} */
export const SidebarContext = createContext();

/**
 * Context provider that wraps the application or specific components
 * and provides access to the sidebar context value.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components that will have access to the context state.
 * @returns {JSX.Element} The Context Provider component.
 */
export const SidebarProvider = ({ children }) => {
  const [activeSection, setActiveSection] = useState(null);

  return (
    <SidebarContext.Provider value={{ activeSection, setActiveSection }}>
      {children}
    </SidebarContext.Provider>
  );
};
